// #7.14 — Dialog CMS (Admin Portal)
//
// CRUD UI for mascot dialogs. Non-technical users can create, edit,
// activate/deactivate, and delete dialogs with a mascot preview,
// trigger selection, and token help.
//
// Backend API: /v1/mascot/admin/dialogs (eat-navigation Worker)
import { useEffect, useState } from 'react';
import { opsApi, getErrorMessage, type MascotDialog, type CreateMascotDialogBody } from '../lib/api';

const CHARACTERS = ['chef_bro', 'the_critic', 'banter'] as const;
const TRIGGERS = [
  'on_app_open',
  'on_empty_state',
  'on_checkin_success',
  'on_streak_milestone',
  'on_level_up',
  'on_seasonal_event',
  'on_live_ops_event',
  'on_badge_unlock',
  'on_mayor_gained',
  'on_rating_submitted',
  'on_first_save',
  'on_collection_created',
  'on_paywall_open',
  'on_return_after_absence',
  'on_sponsored_venue',
  'on_streak_rescue',
  'on_leaderboard_rivalry',
  'on_collections_completion',
  'on_birthday',
  'on_reengagement',
  'on_rewarded_ad_moment',
  'on_mayorship_challenge',
] as const;
const MOODS = ['greeting', 'thinking', 'happy', 'hungry', 'sleeping', 'oops', 'celebration', 'excited'] as const;
const POSITIONS = ['center', 'bottom_left', 'center_bottom', 'near_save_button', 'near_leaderboard', 'sheet_header'] as const;
const VARIANTS = ['control', 'friendly', 'humorous', 'concise'] as const;
const CTA_ACTIONS = ['open_paywall', 'start_rewarded_ad', 'navigate', 'save_streak', 'dismiss'] as const;
const LOCALES = ['tr', 'en'] as const;

const CHARACTER_EMOJI: Record<string, string> = {
  chef_bro: '👨‍🍳',
  the_critic: '👩‍⚖️',
  banter: '💬',
};

export default function MascotDialogs() {
  const [dialogs, setDialogs] = useState<MascotDialog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [filterLocale, setFilterLocale] = useState('');
  const [filterTrigger, setFilterTrigger] = useState('');
  const [filterActive, setFilterActive] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await opsApi.listMascotDialogs({
        locale: filterLocale || undefined,
        trigger: filterTrigger || undefined,
        active: filterActive || undefined,
        limit: 200,
      });
      setDialogs(data.dialogs);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterLocale, filterTrigger, filterActive]);

  const handleDelete = async (id: string) => {
    if (!confirm(`Delete dialog "${id}"? This will deactivate it (is_active = 0).`)) return;
    setActionError('');
    try {
      await opsApi.deleteMascotDialog(id);
      await load();
    } catch (err) {
      setActionError(getErrorMessage(err));
    }
  };

  const handleToggleActive = async (dialog: MascotDialog) => {
    setActionError('');
    try {
      if (dialog.is_active) {
        await opsApi.deactivateMascotDialog(dialog.id);
      } else {
        await opsApi.activateMascotDialog(dialog.id);
      }
      await load();
    } catch (err) {
      setActionError(getErrorMessage(err));
    }
  };

  if (loading) return <div className="text-gray-400">Loading mascot dialogs...</div>;
  if (error) return <div className="text-red-400">Error: {error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Mascot Dialogs</h2>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded text-sm font-medium"
        >
          {showCreate ? 'Cancel' : '+ New Dialog'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center text-sm">
        <label className="text-gray-400">Locale:</label>
        <select
          value={filterLocale}
          onChange={(e) => setFilterLocale(e.target.value)}
          className="px-2 py-1 bg-gray-800 border border-gray-700 rounded text-sm"
        >
          <option value="">All</option>
          {LOCALES.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <label className="text-gray-400 ml-3">Trigger:</label>
        <select
          value={filterTrigger}
          onChange={(e) => setFilterTrigger(e.target.value)}
          className="px-2 py-1 bg-gray-800 border border-gray-700 rounded text-sm"
        >
          <option value="">All</option>
          {TRIGGERS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="flex items-center gap-1 ml-3 text-gray-400">
          <input
            type="checkbox"
            checked={filterActive}
            onChange={(e) => setFilterActive(e.target.checked)}
            className="accent-brand-600"
          />
          Active only
        </label>
      </div>

      {showCreate && <CreateDialogForm onCreated={() => { setShowCreate(false); load(); }} />}

      {actionError && (
        <div className="bg-red-900/20 border border-red-800/50 text-red-200 rounded p-3 text-sm">
          {actionError}
        </div>
      )}

      {dialogs.length === 0 ? (
        <p className="text-gray-500">No mascot dialogs found. Create one to get started.</p>
      ) : (
        <div className="space-y-3">
          {dialogs.map((dialog) => (
            <DialogCard
              key={dialog.id}
              dialog={dialog}
              onDelete={() => handleDelete(dialog.id)}
              onToggleActive={() => handleToggleActive(dialog)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DialogCard({
  dialog,
  onDelete,
  onToggleActive,
}: {
  dialog: MascotDialog;
  onDelete: () => void;
  onToggleActive: () => void;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <EditDialogForm
        dialog={dialog}
        onSaved={() => { setEditing(false); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xl">{CHARACTER_EMOJI[dialog.character] ?? '❓'}</span>
            <h3 className="text-lg font-semibold">{dialog.id}</h3>
            <span
              className={`text-xs px-2 py-0.5 rounded ${
                dialog.is_active ? 'bg-green-900 text-green-300' : 'bg-gray-800 text-gray-400'
              }`}
            >
              {dialog.is_active ? 'Active' : 'Inactive'}
            </span>
            {dialog.sponsor_id && (
              <span className="text-xs px-2 py-0.5 rounded bg-yellow-900/50 text-yellow-300">
                Sponsored: {dialog.sponsor_id}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-300 mt-2 italic">"{dialog.text_template}"</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-sm">
            <div><span className="text-gray-500">Character:</span> {dialog.character}</div>
            <div><span className="text-gray-500">Trigger:</span> {dialog.trigger_type}</div>
            <div><span className="text-gray-500">Locale:</span> {dialog.locale}</div>
            <div><span className="text-gray-500">Mood:</span> {dialog.mood}</div>
            <div><span className="text-gray-500">Animation:</span> {dialog.animation ?? '—'}</div>
            <div><span className="text-gray-500">Position:</span> {dialog.position_hint}</div>
            <div><span className="text-gray-500">Variant:</span> {dialog.variant}</div>
            <div><span className="text-gray-500">Version:</span> {dialog.version}</div>
            <div><span className="text-gray-500">Priority:</span> {dialog.priority}</div>
            <div><span className="text-gray-500">CTA:</span> {dialog.cta_label ?? '—'}</div>
            {dialog.valid_until_ms > 0 && (
              <div>
                <span className="text-gray-500">Valid until:</span>{' '}
                {new Date(dialog.valid_until_ms).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 ml-4">
          <button
            onClick={onToggleActive}
            className={`text-sm px-3 py-1 rounded ${
              dialog.is_active
                ? 'text-yellow-400 hover:text-yellow-300'
                : 'text-green-400 hover:text-green-300'
            }`}
          >
            {dialog.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => setEditing(true)}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            Edit
          </button>
          <button
            onClick={onDelete}
            className="text-sm text-red-400 hover:text-red-300"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateDialogForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState<CreateMascotDialogBody>({
    id: '',
    character: 'chef_bro',
    trigger_type: 'on_app_open',
    trigger_conditions: '{}',
    locale: 'tr',
    text_template: '',
    mood: 'happy',
    animation: '',
    position_hint: 'center',
    cta_label: '',
    cta_action: '',
    cta_payload: '',
    variant: 'control',
    version: '1.0.0',
    is_active: 1,
    valid_from_ms: 0,
    valid_until_ms: 0,
    priority: 50,
    sponsor_id: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.id.trim() || !form.text_template.trim()) {
      setError('ID and text template are required');
      return;
    }
    setSubmitting(true);
    try {
      const body: CreateMascotDialogBody = {
        ...form,
        animation: form.animation || null,
        cta_label: form.cta_label || null,
        cta_action: form.cta_action || null,
        cta_payload: form.cta_payload || null,
        sponsor_id: form.sponsor_id || null,
      };
      await opsApi.createMascotDialog(body);
      onCreated();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
      <h3 className="text-lg font-semibold">Create New Dialog</h3>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <DialogFormFields form={form} setForm={(f) => setForm(f as CreateMascotDialogBody)} />
      <button
        type="submit"
        disabled={submitting}
        className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded text-sm font-medium"
      >
        {submitting ? 'Creating...' : 'Create Dialog'}
      </button>
    </form>
  );
}

function EditDialogForm({
  dialog,
  onSaved,
  onCancel,
}: {
  dialog: MascotDialog;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<Partial<CreateMascotDialogBody>>({
    character: dialog.character,
    trigger_type: dialog.trigger_type,
    trigger_conditions: dialog.trigger_conditions,
    locale: dialog.locale,
    text_template: dialog.text_template,
    mood: dialog.mood,
    animation: dialog.animation ?? '',
    position_hint: dialog.position_hint,
    cta_label: dialog.cta_label ?? '',
    cta_action: dialog.cta_action ?? '',
    cta_payload: dialog.cta_payload ?? '',
    variant: dialog.variant,
    version: dialog.version,
    priority: dialog.priority,
    sponsor_id: dialog.sponsor_id ?? '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const body: Partial<CreateMascotDialogBody> = { ...form };
      // Convert empty strings to null for optional fields
      if (typeof body.animation === 'string' && body.animation === '') body.animation = null;
      if (typeof body.cta_label === 'string' && body.cta_label === '') body.cta_label = null;
      if (typeof body.cta_action === 'string' && body.cta_action === '') body.cta_action = null;
      if (typeof body.cta_payload === 'string' && body.cta_payload === '') body.cta_payload = null;
      if (typeof body.sponsor_id === 'string' && body.sponsor_id === '') body.sponsor_id = null;
      await opsApi.updateMascotDialog(dialog.id, body);
      onSaved();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Edit: {dialog.id}</h3>
        <button type="button" onClick={onCancel} className="text-sm text-gray-400 hover:text-gray-200">Cancel</button>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <DialogFormFields form={form} setForm={setForm} />
      <button
        type="submit"
        disabled={submitting}
        className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded text-sm font-medium"
      >
        {submitting ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
}

function DialogFormFields({
  form,
  setForm,
}: {
  form: Partial<CreateMascotDialogBody>;
  setForm: (f: Partial<CreateMascotDialogBody>) => void;
}) {
  const update = (field: keyof CreateMascotDialogBody, value: string | number) => {
    setForm({ ...form, [field]: value });
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* ID (only for create) */}
      {!form.id && (
        <div className="col-span-2">
          <label className="block text-sm text-gray-400 mb-1">ID (unique, e.g. "chef_bro.checkin.tr")</label>
          <input
            value={form.id ?? ''}
            onChange={(e) => update('id', e.target.value)}
            placeholder="chef_bro.checkin.tr"
            className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm font-mono"
            required
          />
        </div>
      )}

      {/* Character */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Character</label>
        <select
          value={form.character ?? 'chef_bro'}
          onChange={(e) => update('character', e.target.value)}
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
        >
          {CHARACTERS.map((c) => <option key={c} value={c}>{CHARACTER_EMOJI[c]} {c}</option>)}
        </select>
      </div>

      {/* Trigger */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Trigger Type</label>
        <select
          value={form.trigger_type ?? 'on_app_open'}
          onChange={(e) => update('trigger_type', e.target.value)}
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
        >
          {TRIGGERS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Locale */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Locale</label>
        <select
          value={form.locale ?? 'tr'}
          onChange={(e) => update('locale', e.target.value)}
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
        >
          {LOCALES.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      {/* Mood */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Mood</label>
        <select
          value={form.mood ?? 'happy'}
          onChange={(e) => update('mood', e.target.value)}
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
        >
          {MOODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {/* Animation */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Animation (sprite sheet name)</label>
        <input
          value={form.animation ?? ''}
          onChange={(e) => update('animation', e.target.value)}
          placeholder="excited_bounce"
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm font-mono"
        />
      </div>

      {/* Position */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Position Hint</label>
        <select
          value={form.position_hint ?? 'center'}
          onChange={(e) => update('position_hint', e.target.value)}
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
        >
          {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Variant */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">A/B Variant</label>
        <select
          value={form.variant ?? 'control'}
          onChange={(e) => update('variant', e.target.value)}
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
        >
          {VARIANTS.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      {/* Priority */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Priority (higher = shown first)</label>
        <input
          type="number"
          value={form.priority ?? 50}
          onChange={(e) => update('priority', parseInt(e.target.value, 10) || 50)}
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
        />
      </div>

      {/* Version */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">Version (semantic)</label>
        <input
          value={form.version ?? '1.0.0'}
          onChange={(e) => update('version', e.target.value)}
          placeholder="1.0.0"
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm font-mono"
        />
      </div>

      {/* Text Template */}
      <div className="col-span-2">
        <label className="block text-sm text-gray-400 mb-1">
          Text Template (use {'{address}'}, {'{streak}'}, {'{level}'}, {'{gs}'}, {'{rank}'}, etc.)
        </label>
        <textarea
          value={form.text_template ?? ''}
          onChange={(e) => update('text_template', e.target.value)}
          placeholder="{address}, gömdün! Afiyet olsun! 🔥"
          rows={3}
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
          required
        />
      </div>

      {/* Trigger Conditions */}
      <div className="col-span-2">
        <label className="block text-sm text-gray-400 mb-1">Trigger Conditions (JSON)</label>
        <input
          value={form.trigger_conditions ?? '{}'}
          onChange={(e) => update('trigger_conditions', e.target.value)}
          placeholder='{"min_streak": 3}'
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm font-mono"
        />
      </div>

      {/* CTA */}
      <div>
        <label className="block text-sm text-gray-400 mb-1">CTA Label (optional)</label>
        <input
          value={form.cta_label ?? ''}
          onChange={(e) => update('cta_label', e.target.value)}
          placeholder="Save Streak"
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
        />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">CTA Action</label>
        <select
          value={form.cta_action ?? ''}
          onChange={(e) => update('cta_action', e.target.value)}
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm"
        >
          <option value="">None</option>
          {CTA_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <div className="col-span-2">
        <label className="block text-sm text-gray-400 mb-1">CTA Payload (JSON, optional)</label>
        <input
          value={form.cta_payload ?? ''}
          onChange={(e) => update('cta_payload', e.target.value)}
          placeholder='{"target":"venue"}'
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm font-mono"
        />
      </div>

      {/* Sponsor */}
      <div className="col-span-2">
        <label className="block text-sm text-gray-400 mb-1">Sponsor ID (optional, B2B)</label>
        <input
          value={form.sponsor_id ?? ''}
          onChange={(e) => update('sponsor_id', e.target.value)}
          placeholder="v_abc123"
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm font-mono"
        />
      </div>
    </div>
  );
}
